import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { Terminal } from '@xterm/xterm';

export const TERMINAL_VIEWPORT_DEBUG_QUERY_PARAM = 'terminaldebug';
const TERMINAL_VIEWPORT_DEBUG_LEGACY_QUERY_PARAM = 'terminalDebug';
const TERMINAL_VIEWPORT_DEBUG_HISTORY_LIMIT = 60;
const TERMINAL_VIEWPORT_DEBUG_REPORT_HISTORY_LIMIT = 20;

type RectSnapshot = {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
};

export type TerminalViewportDebugSnapshot = {
  activeElement: string;
  cssViewport: string;
  document: string;
  frame: RectSnapshot | null;
  frameGap: string;
  frameLayout: string;
  fullscreen: boolean;
  inner: string;
  keyboard: string;
  keyboardGuard: string;
  orientation: string;
  panel: RectSnapshot | null;
  screen: RectSnapshot | null;
  screenSize: string;
  sequence: number;
  shortcut: RectSnapshot | null;
  shortcutButtons: string;
  shortcutGrid: string;
  shortcutHitTest: string;
  shortcutPadding: string;
  textarea: string;
  time: number;
  trigger: string;
  virtualKeyboard: string;
  visualViewport: string;
  xterm: RectSnapshot | null;
  xtermScreen: RectSnapshot | null;
  xtermViewport: RectSnapshot | null;
};

type TerminalViewportDebugOptions = {
  isMaximized: boolean;
  panelRef: RefObject<HTMLElement | null>;
  terminalRef: RefObject<Terminal | null>;
};

const rounded = (value: number) => Math.round(value * 10) / 10;

function rectSnapshot(element: Element | null | undefined): RectSnapshot | null {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    bottom: rounded(rect.bottom),
    height: rounded(rect.height),
    left: rounded(rect.left),
    right: rounded(rect.right),
    top: rounded(rect.top),
    width: rounded(rect.width)
  };
}

function elementLabel(element: Element | null): string {
  if (!element) return 'none';
  const htmlElement = element as HTMLElement;
  const id = htmlElement.id ? `#${htmlElement.id}` : '';
  const classes = typeof htmlElement.className === 'string'
    ? htmlElement.className.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((name) => `.${name}`).join('')
    : '';
  return `${htmlElement.tagName.toLowerCase()}${id}${classes}`;
}

export function terminalViewportDebugRequested(search: string): boolean {
  const params = new URLSearchParams(search);
  return params.get(TERMINAL_VIEWPORT_DEBUG_QUERY_PARAM) === '1' ||
    params.get(TERMINAL_VIEWPORT_DEBUG_LEGACY_QUERY_PARAM) === '1';
}

function captureTerminalViewportDebugSnapshot(
  sequence: number,
  trigger: string,
  panel: HTMLElement,
  terminal: Terminal | null
): TerminalViewportDebugSnapshot {
  const frameElement = panel.querySelector<HTMLElement>('.terminal-panel__viewport-frame');
  const screenElement = panel.querySelector<HTMLElement>('.terminal-panel__screen');
  const shortcutElement = panel.querySelector<HTMLElement>('.terminal-panel__shortcut-bar');
  const xtermElement = screenElement?.querySelector<HTMLElement>('.xterm');
  const xtermScreenElement = screenElement?.querySelector<HTMLElement>('.xterm-screen');
  const xtermViewportElement = screenElement?.querySelector<HTMLElement>('.xterm-viewport');
  const panelRect = rectSnapshot(panel);
  const frameDisplay = frameElement ? window.getComputedStyle(frameElement).display : 'missing';
  // display:contents intentionally removes the redundant frame box. Report
  // the fullscreen panel as the effective frame so geometry remains directly
  // comparable with older diagnostic versions.
  const frameRect = frameDisplay === 'contents' ? panelRect : rectSnapshot(frameElement);
  const shortcutStyle = shortcutElement ? window.getComputedStyle(shortcutElement) : null;
  const shortcutButtons = shortcutElement
    ? Array.from(shortcutElement.querySelectorAll<HTMLButtonElement>('button')).filter(
      (button) => window.getComputedStyle(button).display !== 'none'
    )
    : [];
  const shortcutButtonGeometry = shortcutButtons.map((button) => ({
    button,
    rect: button.getBoundingClientRect()
  }));
  const hitButtons = shortcutButtonGeometry.filter(({ button, rect }) => {
    const hit = panel.ownerDocument.elementFromPoint(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2
    );
    return hit === button || button.contains(hit);
  });
  const viewport = window.visualViewport;
  const virtualKeyboard = (navigator as Navigator & {
    virtualKeyboard?: { boundingRect: DOMRectReadOnly; overlaysContent: boolean };
  }).virtualKeyboard;
  const virtualKeyboardRect = virtualKeyboard?.boundingRect;
  const textarea = terminal?.textarea;
  const activeElement = panel.ownerDocument.activeElement;
  const panelStyle = panel.style;

  return {
    activeElement: elementLabel(activeElement),
    cssViewport: [
      panelStyle.getPropertyValue('--terminal-visual-viewport-width').trim() || '—',
      panelStyle.getPropertyValue('--terminal-visual-viewport-height').trim() || '—',
      panelStyle.getPropertyValue('--terminal-visual-viewport-offset-left').trim() || '—',
      panelStyle.getPropertyValue('--terminal-visual-viewport-offset-top').trim() || '—'
    ].join('/'),
    document: `${document.documentElement.clientWidth}x${document.documentElement.clientHeight}`,
    frame: frameRect,
    frameGap: panelRect && frameRect
      ? `t${rounded(frameRect.top - panelRect.top)} b${rounded(panelRect.bottom - frameRect.bottom)}`
      : '—',
    frameLayout: `${panel.dataset.terminalViewportFrame ?? 'unknown'} display=${frameDisplay}`,
    fullscreen: document.fullscreenElement === panel,
    inner: `${window.innerWidth}x${window.innerHeight}`,
    keyboard: panel.dataset.terminalKeyboard ?? 'unknown',
    keyboardGuard: panel.dataset.terminalKeyboardGuard ?? 'unknown',
    orientation: `${window.screen.orientation?.type ?? 'unknown'}:${window.screen.orientation?.angle ?? '—'}`,
    panel: panelRect,
    screen: rectSnapshot(screenElement),
    screenSize: `${window.screen.width}x${window.screen.height}/${window.screen.availHeight}@${window.devicePixelRatio}`,
    sequence,
    shortcut: rectSnapshot(shortcutElement),
    shortcutButtons: shortcutButtonGeometry.map(({ button, rect }) =>
      `${button.textContent?.trim() || button.getAttribute('aria-label') || '?'}@` +
      `${rounded(rect.left)},${rounded(rect.top)}:${rounded(rect.width)}x${rounded(rect.height)}`
    ).join('|') || '—',
    shortcutGrid: shortcutStyle
      ? `rows=${shortcutStyle.gridTemplateRows} cols=${shortcutStyle.gridTemplateColumns}` +
        ` gap=${shortcutStyle.rowGap}/${shortcutStyle.columnGap}` +
        ` pos=${shortcutStyle.position} z=${shortcutStyle.zIndex}` +
        ` contain=${shortcutStyle.contain}`
      : '—',
    shortcutHitTest: `${hitButtons.length}/${shortcutButtons.length}`,
    shortcutPadding: shortcutStyle
      ? `${shortcutStyle.paddingTop}/${shortcutStyle.paddingRight}/${shortcutStyle.paddingBottom}/${shortcutStyle.paddingLeft}`
      : '—',
    textarea: textarea
      ? `${activeElement === textarea ? 'focused' : 'blurred'} mode=${textarea.inputMode || 'default'} ro=${textarea.readOnly}`
      : 'missing',
    time: rounded(window.performance.now()),
    trigger,
    virtualKeyboard: virtualKeyboard && virtualKeyboardRect
      ? `overlays=${virtualKeyboard.overlaysContent}` +
        ` rect=${rounded(virtualKeyboardRect.x)},${rounded(virtualKeyboardRect.y)}` +
        ` ${rounded(virtualKeyboardRect.width)}x${rounded(virtualKeyboardRect.height)}`
      : 'missing',
    visualViewport: viewport
      ? `${rounded(viewport.width)}x${rounded(viewport.height)}+${rounded(viewport.offsetLeft)},${rounded(viewport.offsetTop)}` +
        ` page=${rounded(viewport.pageLeft)},${rounded(viewport.pageTop)} scale=${rounded(viewport.scale)}`
      : 'missing',
    xterm: rectSnapshot(xtermElement),
    xtermScreen: rectSnapshot(xtermScreenElement),
    xtermViewport: rectSnapshot(xtermViewportElement)
  };
}

/** Produces one line with no Enter, safe to place at an interactive prompt. */
export function terminalViewportDebugReport(
  snapshots: readonly TerminalViewportDebugSnapshot[],
  userAgent: string
): string {
  const latest = snapshots.at(-1) ?? null;
  const recent = snapshots.slice(-TERMINAL_VIEWPORT_DEBUG_REPORT_HISTORY_LIMIT).map((snapshot) => ({
    event: snapshot.trigger,
    frameGap: snapshot.frameGap,
    frameLayout: snapshot.frameLayout,
    keyboard: snapshot.keyboard,
    keyboardGuard: snapshot.keyboardGuard,
    layers: `x${snapshot.xterm?.bottom ?? '—'} s${snapshot.shortcut?.top ?? '—'}-${snapshot.shortcut?.bottom ?? '—'}`,
    sequence: snapshot.sequence,
    shortcutHitTest: snapshot.shortcutHitTest,
    textarea: snapshot.textarea,
    time: snapshot.time,
    virtualKeyboard: snapshot.virtualKeyboard,
    visualViewport: snapshot.visualViewport
  }));
  return `[terminal-debug] ${JSON.stringify({
    version: 5,
    capturedAt: new Date().toISOString(),
    userAgent,
    latest,
    recent
  })}`;
}

/**
 * Records mobile fullscreen geometry only when `?terminaldebug=1` is present.
 * The returned action pastes a compact one-line report through xterm without
 * appending Enter, leaving the user in control of whether it is submitted.
 */
export function useTerminalViewportDebug({
  isMaximized,
  panelRef,
  terminalRef
}: TerminalViewportDebugOptions) {
  const requested = typeof window !== 'undefined' && terminalViewportDebugRequested(window.location.search);
  const enabled = requested && isMaximized;
  const snapshotsRef = useRef<TerminalViewportDebugSnapshot[]>([]);
  const sequenceRef = useRef(0);

  const record = useCallback((trigger: string) => {
    const panel = panelRef.current;
    if (!requested || !isMaximized || !panel) return;
    snapshotsRef.current = [
      ...snapshotsRef.current,
      captureTerminalViewportDebugSnapshot(
        ++sequenceRef.current,
        trigger,
        panel,
        terminalRef.current
      )
    ].slice(-TERMINAL_VIEWPORT_DEBUG_HISTORY_LIMIT);
  }, [isMaximized, panelRef, requested, terminalRef]);

  useEffect(() => {
    if (!enabled) return;
    const panel = panelRef.current;
    if (!panel) return;
    const viewport = window.visualViewport;
    const virtualKeyboard = (navigator as Navigator & {
      virtualKeyboard?: EventTarget;
    }).virtualKeyboard;
    let animationFrame: number | null = null;
    const pendingTriggers = new Set<string>();
    const scheduleRecord = (trigger: string) => {
      pendingTriggers.add(trigger);
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(() => {
        animationFrame = null;
        const triggers = [...pendingTriggers].join('+');
        pendingTriggers.clear();
        record(triggers);
      });
    };
    const onViewportEvent = (event: Event) => scheduleRecord(`vv:${event.type}`);
    const onVirtualKeyboardEvent = (event: Event) => scheduleRecord(`vk:${event.type}`);
    const onWindowEvent = (event: Event) => scheduleRecord(`window:${event.type}`);
    const onDocumentEvent = (event: Event) => {
      record(`${event.type}:${elementLabel(event.target as Element | null)}`);
    };
    const onTouchEvent = (event: Event) => record(`${event.type}:${elementLabel(event.target as Element | null)}`);

    viewport?.addEventListener('resize', onViewportEvent);
    viewport?.addEventListener('scroll', onViewportEvent);
    viewport?.addEventListener('scrollend', onViewportEvent);
    virtualKeyboard?.addEventListener('geometrychange', onVirtualKeyboardEvent);
    window.addEventListener('resize', onWindowEvent);
    window.addEventListener('orientationchange', onWindowEvent);
    document.addEventListener('fullscreenchange', onDocumentEvent, true);
    document.addEventListener('focusin', onDocumentEvent, true);
    document.addEventListener('focusout', onDocumentEvent, true);
    panel.addEventListener('touchstart', onTouchEvent, true);
    panel.addEventListener('touchend', onTouchEvent, true);
    panel.addEventListener('touchcancel', onTouchEvent, true);

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver((entries) => {
        const labels = entries.map((entry) => elementLabel(entry.target)).join(',');
        scheduleRecord(`resize-observer:${labels}`);
      });
    resizeObserver?.observe(panel);
    for (const selector of [
      '.terminal-panel__viewport-frame',
      '.terminal-panel__screen',
      '.terminal-panel__shortcut-bar'
    ]) {
      const element = panel.querySelector(selector);
      if (element) resizeObserver?.observe(element);
    }

    scheduleRecord('debug-mounted');
    return () => {
      viewport?.removeEventListener('resize', onViewportEvent);
      viewport?.removeEventListener('scroll', onViewportEvent);
      viewport?.removeEventListener('scrollend', onViewportEvent);
      virtualKeyboard?.removeEventListener('geometrychange', onVirtualKeyboardEvent);
      window.removeEventListener('resize', onWindowEvent);
      window.removeEventListener('orientationchange', onWindowEvent);
      document.removeEventListener('fullscreenchange', onDocumentEvent, true);
      document.removeEventListener('focusin', onDocumentEvent, true);
      document.removeEventListener('focusout', onDocumentEvent, true);
      panel.removeEventListener('touchstart', onTouchEvent, true);
      panel.removeEventListener('touchend', onTouchEvent, true);
      panel.removeEventListener('touchcancel', onTouchEvent, true);
      resizeObserver?.disconnect();
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
    };
  }, [enabled, panelRef, record]);

  const pasteReport = useCallback(() => {
    const terminal = terminalRef.current;
    if (!enabled || !terminal) return false;
    record('debug-button');
    terminal.paste(terminalViewportDebugReport(snapshotsRef.current, navigator.userAgent));
    return true;
  }, [enabled, record, terminalRef]);

  return { enabled, pasteReport };
}
